import * as React from 'react';

import { JRCFormSelectFieldRender, JRCFormSelectProps } from './JRCFormSelect';
import { JRCTag } from 'components/JRCTag/JRCTag';
import { useIntl } from 'react-intl';
import { Field, WrappedFieldInputProps } from 'redux-form';
import JRCFormFieldRender, { InputState } from '../JRCFormFieldRender';
import { components, MultiValueProps, OptionProps, OptionTypeBase, SingleValueProps } from 'react-select';
import styled, { ThemeContext } from 'styled-components';
import { FieldValues } from 'react-hook-form';
import { useControls } from '../ReduxFormControls';

const customStyle = {
    menuList: {
        padding: '0',
        borderRadius: '8px',
    },
    valueContainer: {
        padding: '0px 8px',
    },
    group: {
        padding: '0',
        marginBottom: '0.25em',
    },
};

const getOptionLabel = <isMulti extends boolean>(
    props: OptionProps<OptionTypeBase, isMulti> | SingleValueProps<OptionTypeBase> | MultiValueProps<OptionTypeBase>,
) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return props.selectProps.getOptionLabel(props.data);
};

const JRCFormSelectTagRenderer = <T extends FieldValues = FieldValues>(
    props: JRCFormSelectProps<T>,
    input: WrappedFieldInputProps,
    inputState: InputState,
) => {
    const { components, ...otherProps } = props;
    const { fallbackUrl } = React.useContext(ThemeContext);

    return (
        <JRCFormSelectFieldRender
            components={{
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                MultiValue: (props: MultiValueProps<OptionTypeBase>) => (
                    <JRCTag
                        onMouseDown={props.removeProps.onMouseDown}
                        onClick={(e) => props.removeProps.onClick(e)}
                        focusable={true}
                        clickVariant="delete"
                        isFocused={props.isFocused}
                        uri={props.data.uri}
                        label={getOptionLabel(props)}
                        fallbackUrl={fallbackUrl.article}
                        collapsed={input.value && input.value.length > 2}
                        tooltipDescription={getOptionLabel(props)}
                    />
                ),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                SingleValue: (props: SingleValueProps<OptionTypeBase>) => (
                    <JRCTag
                        uri={props.data.uri}
                        label={getOptionLabel(props)}
                        clickVariant="delete"
                        fallbackUrl={fallbackUrl.article}
                    />
                ),
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                Option,
                ...components,
            }}
            input={input}
            inputState={inputState}
            customReactSelectStyle={customStyle}
            {...otherProps}
        />
    );
};

/**
 * FIX: For an obscure reason, when the method is directly defined in the component props, we get an error
 * @param props
 * @returns
 */
const Option = <isMulti extends boolean>(props: OptionProps<OptionTypeBase, isMulti>) => {
    const { fallbackUrl } = React.useContext(ThemeContext);

    return (
        <components.Option {...props}>
            <FullLength>
                <JRCTag
                    uri={props.data.uri}
                    focusable={true}
                    clickVariant="select"
                    isFocused={props.isFocused}
                    label={getOptionLabel(props)}
                    fallbackUrl={fallbackUrl.article}
                />
            </FullLength>
        </components.Option>
    );
};

const FullLength = styled.div`
    & > * {
        width: calc(100% - 28px);
        margin: 2px 6px;
        min-height: 32px;
    }
`;

/**
 * Component used as a select sync or async with custom display: options are JRCTags
 * options (or asyncPromise) elements should have a uri and a name attributes
 * @param props JRCFormSelectProps
 * validation props: required
 * @returns JSX.Element
 * @deprecated Use JRCInputSelect alternatives instead
 */
const JRCFormSelectTagField = <TValue extends FieldValues = FieldValues>(props: JRCFormSelectProps<TValue>) => {
    const validate = useControls({
        intl: useIntl(),
        required: props.required,
    });
    return (
        <Field
            component={JRCFormFieldRender}
            renderInputFct={(input: WrappedFieldInputProps, inputState: InputState) =>
                JRCFormSelectTagRenderer(props, input, inputState)
            }
            validate={validate}
            htmlFor={props.name}
            {...props}
        />
    );
};

export { JRCFormSelectTagField };
